import { produce } from "immer";
import { createSelector } from "reselect";
import { UserAccountStatus, FeatureAccessStatus, BulkOperationStatus } from "../../contract/pam.js";
import { getErrorCodeFromMessage } from "../../redux-folder/error-handling/utils.js";
import "../../redux-folder/error-handling/action.js";
import { BulkItemStatus, failedOperationId, BulkActionKey, bulkItemFailedStatues } from "./types.js";
const itemsSelector = (state) => state.bulkActions.state.selectedItems;
const validationResultsSelector = (state) => state.bulkActions.state.validationResults;
const actionsDataSelector = (state) => state.bulkActions.state.actionsData;
const actionsDataWithoutSkippedItemsSelector = createSelector(
  (state) => actionsDataSelector(state),
  (allActions) => {
    return produce(allActions, (draftState) => {
      Object.keys(draftState).forEach((actionKey) => {
        draftState[actionKey] = draftState[actionKey]?.filter((i) => i.status !== BulkItemStatus.Skipped);
        if (draftState[actionKey]?.length === 0) {
          delete draftState[actionKey];
        }
      });
    });
  }
);
const actionItemsSelector = createSelector(
  (state) => actionsDataSelector(state),
  (_, bulkActionKey) => bulkActionKey,
  (allActions, actionKey) => {
    return Object.values(allActions)?.flatMap((i) => i)?.filter((i) => i.actionKey === actionKey);
  }
);
const simpleResultValueSelector = (state, actionKey) => {
  const allActions = actionsDataSelector(state);
  const actionsWithKey = allActions[actionKey];
  return (actionsWithKey ?? [])[0]?.value;
};
const accountLockInitialValueSelector = (state) => {
  const selectedItems = itemsSelector(state);
  return selectedItems?.map((i) => i.account_status === UserAccountStatus.Locked);
};
const accountLockResultValueSelector = (state) => {
  const allActions = actionsDataWithoutSkippedItemsSelector(state);
  const actionsWithKey = allActions[BulkActionKey.AccountLock] ?? [];
  return actionsWithKey?.find((i) => i.value?.isLocked !== null)?.value;
};
const withdrawalLockInitialValueSelector = (state) => {
  const selectedItems = itemsSelector(state);
  return selectedItems?.map((i) => i.withdrawal_status === FeatureAccessStatus.Locked);
};
const withdrawalLockResultValueSelector = (state) => {
  const allActions = actionsDataWithoutSkippedItemsSelector(state);
  const actionsWithKey = allActions[BulkActionKey.WithdrawalLock] ?? [];
  return actionsWithKey?.find((i) => i.value?.isLocked !== null)?.value;
};
const depositLockInitialValueSelector = (state) => {
  const selectedItems = itemsSelector(state);
  return selectedItems?.map((i) => i.deposit_status === FeatureAccessStatus.Locked);
};
const depositLockResultValueSelector = (state) => {
  const allActions = actionsDataWithoutSkippedItemsSelector(state);
  const actionsWithKey = allActions[BulkActionKey.DepositLock] ?? [];
  return actionsWithKey?.find((i) => i.value?.isLocked !== null)?.value;
};
const lobbyLockInitialValueSelector = (state) => {
  const selectedItems = itemsSelector(state);
  return selectedItems?.map((i) => i.lobby_access_status === FeatureAccessStatus.Locked);
};
const lobbyLockResultValueSelector = (state) => {
  const allActions = actionsDataWithoutSkippedItemsSelector(state);
  const actionsWithKey = allActions[BulkActionKey.LobbyLock] ?? [];
  return actionsWithKey?.find((i) => i.value?.isLocked !== null)?.value;
};
const casinoLockResultValueSelector = (state) => {
  return simpleResultValueSelector(state, BulkActionKey.CasinoLock)?.feature_status;
};
const sportsbookLockResultValueSelector = (state) => {
  return simpleResultValueSelector(state, BulkActionKey.SportsbookLock)?.feature_status;
};
const messageResultValueSelector = (state) => {
  return simpleResultValueSelector(state, BulkActionKey.Message)?.message;
};
const notesAttachmentsResultValueSelector = (state) => {
  return simpleResultValueSelector(state, BulkActionKey.NotesAndAttachments);
};
const securityCaseInitialValueSelector = (state) => {
  const selectedItems = itemsSelector(state);
  const allSecurityCases = selectedItems?.reduce((result, item) => {
    return item.security_cases ? [...result, ...item.security_cases] : result;
  }, []);
  const caseOccurrenceMap = {};
  allSecurityCases?.forEach((securityCase) => {
    const securityCaseKey = `${securityCase.type}-${securityCase.case_id}`;
    if (!caseOccurrenceMap[securityCaseKey]) {
      caseOccurrenceMap[securityCaseKey] = { case: securityCase, occurrences: 1 };
    } else {
      caseOccurrenceMap[securityCaseKey].occurrences++;
    }
  });
  const initialSecurityCases = Object.values(caseOccurrenceMap)?.map((item) => ({
    ...item.case,
    totalCount: item.occurrences
  }));
  return selectedItems?.length ? initialSecurityCases : [];
};
const securityCaseResultValueSelector = (state, actionKey) => {
  const allActions = actionsDataSelector(state);
  const actionsWithKey = allActions[actionKey];
  const submitModel = { initial: [], add: [], remove: [] };
  const addCaseIfNotAlreadyExist = (submitCases, securityCase) => {
    const isCaseAlreadyExist = submitCases?.find((c) => c.case_id === securityCase.case_id && c.type === securityCase.type);
    if (submitCases && !isCaseAlreadyExist) {
      submitCases.push(securityCase);
    }
  };
  actionsWithKey?.forEach((action) => {
    const actionResultValue = action?.value;
    actionResultValue?.add?.forEach((securityCase) => {
      addCaseIfNotAlreadyExist(submitModel.add, securityCase);
    });
    actionResultValue?.remove?.forEach((securityCase) => {
      addCaseIfNotAlreadyExist(submitModel.remove, securityCase);
    });
  });
  const hasChanges = submitModel?.add?.length || submitModel?.remove?.length;
  return hasChanges ? submitModel : void 0;
};
const securityCaseAddResultValueSelector = (state) => {
  return securityCaseResultValueSelector(state, BulkActionKey.SecurityCasesAdd);
};
const securityCaseUpdateResultValueSelector = (state) => {
  return securityCaseResultValueSelector(state, BulkActionKey.SecurityCasesUpdate);
};
const securityCaseRemoveResultValueSelector = (state) => {
  return securityCaseResultValueSelector(state, BulkActionKey.SecurityCasesRemove);
};
const securityCaseAllResultValueSelector = (state) => {
  const addItems = securityCaseAddResultValueSelector(state);
  const updateItems = securityCaseUpdateResultValueSelector(state);
  const removeItems = securityCaseRemoveResultValueSelector(state);
  return {
    addedCases: addItems,
    updatedCases: updateItems,
    removedCases: removeItems
  };
};
const manualTriggerResultValueSelector = (state, actionKey) => {
  const allActions = actionsDataSelector(state);
  const actionsWithKey = allActions[actionKey];
  const resultValue = actionsWithKey?.map((i) => i.value);
  const hasChanges = resultValue?.length;
  return hasChanges ? { bonuses: resultValue } : void 0;
};
const manualTriggerDepositCashMatchResultValueSelector = (state) => {
  return manualTriggerResultValueSelector(state, BulkActionKey.ManualTriggerDepositCashMatch);
};
const manualTriggerImmediateCashBonusResultValueSelector = (state) => {
  return manualTriggerResultValueSelector(state, BulkActionKey.ManualTriggerImmediateCashBonus);
};
const manualTriggerMTTTicketResultValueSelector = (state) => {
  return manualTriggerResultValueSelector(state, BulkActionKey.ManualTriggerMTTTicket);
};
const manualTriggerCasinoCoinBonusResultValueSelector = (state) => {
  return manualTriggerResultValueSelector(state, BulkActionKey.ManualTriggerCasinoCoinBonus);
};
const userLabelResultValueSelector = (state, actionKey) => {
  const allActions = actionItemsSelector(state, actionKey);
  const resultValue = allActions?.map((i) => i.value);
  return resultValue?.[0];
};
const addUserLabelResultValueSelector = (state) => {
  return userLabelResultValueSelector(state, BulkActionKey.AddUserLabel);
};
const removeUserLabelResultValueSelector = (state) => {
  return userLabelResultValueSelector(state, BulkActionKey.RemoveUserLabel);
};
const manualTransactionResultValueSelector = (state) => {
  const allActions = actionsDataSelector(state);
  const actionsWithKey = allActions[BulkActionKey.ManualTransactions];
  return actionsWithKey?.map((i) => i.value);
};
const p2pTransferResultValueSelector = (state) => {
  const allActions = actionsDataSelector(state);
  const actionsWithKey = allActions[BulkActionKey.P2PTransfer];
  return actionsWithKey?.map((i) => i.value);
};
const manualCreditResultValueSelector = (state) => {
  const allActions = actionsDataSelector(state);
  const actionsWithKey = allActions[BulkActionKey.ManualCredit];
  return actionsWithKey?.map((i) => i.value);
};
const bulkOperationsSelector = (state) => state.bulkActions.state.bulkOperations;
const bulkOperationsIdsSelector = createSelector(
  bulkOperationsSelector,
  (state) => Object.fromEntries(Object.entries(state).map(([key, value]) => [key, value.id]))
);
const bulkOperationsErrorsSelector = createSelector(
  bulkOperationsSelector,
  (_, { actionKey }) => actionKey,
  (state, actionKey) => state[actionKey]?.errorCode
);
const bulkOperationsNotFailedIdsSelector = createSelector(
  bulkOperationsIdsSelector,
  (state) => Object.fromEntries(Object.entries(state).filter(([_, value]) => value && value !== failedOperationId))
);
const bulkFailedOperationActionKeysSelector = createSelector(
  bulkOperationsIdsSelector,
  (state) => Object.keys(state).filter((key) => state[key] === failedOperationId)
);
const bulkOperationNotCreatedActionKeys = createSelector(
  bulkOperationsIdsSelector,
  (state) => Object.keys(state)?.filter((key) => state[key] === failedOperationId) ?? []
);
const bulkOperationsFilterSelector = createSelector(
  bulkOperationsNotFailedIdsSelector,
  (state) => Object.values(state).join(",")
);
const isExecutionInProgressSelector = createSelector(
  (_, { operations }) => operations,
  bulkOperationsNotFailedIdsSelector,
  bulkOperationNotCreatedActionKeys,
  actionsDataSelector,
  getExecutionStatusResultSelector(checkOperationIsProcessing, checkActionStateHasProcessingItems)
);
const hasExecutionFailedItemsSelector = createSelector(
  (_, { operations }) => operations,
  bulkOperationsNotFailedIdsSelector,
  bulkOperationNotCreatedActionKeys,
  actionsDataSelector,
  getExecutionStatusResultSelector(checkOperationHasFailedItems, checkActionStateHasFailedItems)
);
const executionItemsSelector = createSelector(
  (_, { operations }) => operations,
  bulkOperationsNotFailedIdsSelector,
  actionsDataWithoutSkippedItemsSelector,
  (operations, createdOperationIdsState, bulkActionItems) => {
    const joinedActionItems = { ...bulkActionItems };
    Object.keys(bulkActionItems).map((actionKey) => {
      const operationId = createdOperationIdsState[actionKey];
      if (operationId) {
        const operation = operations?.find((o) => o.id === operationId);
        joinedActionItems[actionKey] = produce(bulkActionItems[actionKey], (draftState) => {
          draftState.forEach((i) => {
            const operationItem = operation?.items?.find((o) => o.id === i?.itemId);
            i.status = operationItem?.status ?? i.status;
            i.errorCode = getErrorCodeFromMessage(operationItem?.message) ?? i.errorCode;
            i.message = operationItem?.message;
          });
        });
      }
    });
    return joinedActionItems;
  }
);
function getExecutionStatusResultSelector(checkOperation, checkActionItems) {
  function executionStatusResultSelector(operations, createdOperationIdsState, failedOperationKeys, bulkActionItems) {
    let hasItems = false;
    Object.keys(bulkActionItems).map((actionKey) => {
      const operationId = createdOperationIdsState[actionKey];
      if (operationId) {
        const operation = operations?.find((o) => o.id === operationId);
        hasItems = hasItems || checkOperation(operation);
      } else {
        hasItems = hasItems || !failedOperationKeys.includes(actionKey) && checkActionItems(bulkActionItems, actionKey);
      }
    });
    return hasItems;
  }
  return executionStatusResultSelector;
}
function checkOperationHasFailedItems(operation) {
  return operation?.items?.some((i) => bulkItemFailedStatues.includes(i.status));
}
function checkActionStateHasFailedItems(bulkActionItems, actionKey) {
  return bulkActionItems[actionKey]?.some((value) => bulkItemFailedStatues.includes(value.status));
}
function checkOperationIsProcessing(operation) {
  return operation?.status !== BulkOperationStatus.Completed;
}
function checkActionStateHasProcessingItems(bulkActionItems, actionKey) {
  const itemProcessingStatuses = [BulkItemStatus.Pending, BulkItemStatus.InProgress];
  return bulkActionItems[actionKey]?.some((value) => itemProcessingStatuses.includes(value.status));
}
const p2pSettingsResultValueSelector = (state) => {
  return simpleResultValueSelector(state, BulkActionKey.P2PSettings);
};
export {
  accountLockInitialValueSelector,
  accountLockResultValueSelector,
  actionItemsSelector,
  actionsDataSelector,
  actionsDataWithoutSkippedItemsSelector,
  addUserLabelResultValueSelector,
  bulkFailedOperationActionKeysSelector,
  bulkOperationNotCreatedActionKeys,
  bulkOperationsErrorsSelector,
  bulkOperationsFilterSelector,
  bulkOperationsNotFailedIdsSelector,
  casinoLockResultValueSelector,
  depositLockInitialValueSelector,
  depositLockResultValueSelector,
  executionItemsSelector,
  hasExecutionFailedItemsSelector,
  isExecutionInProgressSelector,
  itemsSelector,
  lobbyLockInitialValueSelector,
  lobbyLockResultValueSelector,
  manualCreditResultValueSelector,
  manualTransactionResultValueSelector,
  manualTriggerCasinoCoinBonusResultValueSelector,
  manualTriggerDepositCashMatchResultValueSelector,
  manualTriggerImmediateCashBonusResultValueSelector,
  manualTriggerMTTTicketResultValueSelector,
  messageResultValueSelector,
  notesAttachmentsResultValueSelector,
  p2pSettingsResultValueSelector,
  p2pTransferResultValueSelector,
  removeUserLabelResultValueSelector,
  securityCaseAddResultValueSelector,
  securityCaseAllResultValueSelector,
  securityCaseInitialValueSelector,
  securityCaseRemoveResultValueSelector,
  securityCaseUpdateResultValueSelector,
  simpleResultValueSelector,
  sportsbookLockResultValueSelector,
  validationResultsSelector,
  withdrawalLockInitialValueSelector,
  withdrawalLockResultValueSelector
};
//# sourceMappingURL=selectors.js.map
